import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Users, Package, ShoppingCart, DollarSign,
  AlertTriangle, Calendar, Target,
} from 'lucide-react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const DeltaBadge = ({ pct, positiveIsGood = true }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-muted-foreground">vs prev.</span>;
  }
  const good = positiveIsGood ? pct >= 0 : pct <= 0;
  const Icon = pct >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      <Icon className="w-3 h-3" />{pct > 0 ? '+' : ''}{pct}% vs prev.
    </span>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/reports/dashboard`)
      .then(r => mounted && setStats(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  const mtd = stats?.mtd || {};
  const prev = stats?.previous_month || {};
  const delta = stats?.delta || {};
  const proj = stats?.projection || {};
  const dailySeries = stats?.daily_series || [];

  const cards = [
    { title: `Revenue · ${mtd.month_label || 'This month'}`, value: `RWF ${formatNumber(mtd.revenue)}`, sub: `vs RWF ${formatNumber(prev.mtd_revenue)} same time last month`, delta: delta.revenue_pct, icon: DollarSign, bg: 'bg-emerald-50', color: 'text-emerald-600' },
    { title: 'Purchases MTD', value: `RWF ${formatNumber(mtd.purchases)}`, sub: `Prev month full: RWF ${formatNumber(prev.full_purchases)}`, icon: ShoppingCart, bg: 'bg-orange-50', color: 'text-orange-600' },
    { title: 'Expenses MTD', value: `RWF ${formatNumber(mtd.expenses)}`, sub: `Prev month full: RWF ${formatNumber(prev.full_expenses)}`, icon: TrendingUp, bg: 'bg-red-50', color: 'text-red-600' },
    { title: 'Profit MTD', value: `RWF ${formatNumber(mtd.profit)}`, sub: `vs RWF ${formatNumber(prev.mtd_profit)} same time last month`, delta: delta.profit_pct, icon: DollarSign, bg: 'bg-primary/10', color: 'text-primary' },
    { title: 'MTD Orders', value: (stats?.mtd_orders_count || 0).toLocaleString(), sub: `Day ${mtd.days_elapsed} of ${mtd.days_in_month}`, icon: Calendar, bg: 'bg-blue-50', color: 'text-blue-600' },
    { title: 'Customers · Products', value: `${stats?.total_customers || 0} · ${stats?.total_products || 0}`, sub: 'All-time', icon: Users, bg: 'bg-purple-50', color: 'text-purple-600' },
  ];

  const projCards = [
    { title: 'Projected revenue', v: proj.revenue, tone: 'text-emerald-700' },
    { title: 'Projected purchases', v: proj.purchases, tone: 'text-orange-700' },
    { title: 'Projected expenses', v: proj.expenses, tone: 'text-red-700' },
    { title: 'Projected profit', v: proj.profit, tone: 'text-primary' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">Dashboard Overview</h1>
        <p className="text-muted-foreground">Month-to-date performance · {mtd.month_label} (day {mtd.days_elapsed}/{mtd.days_in_month})</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mtd-cards">
        {cards.map((s, i) => (
          <div key={i} className="bg-white p-5 rounded-xl border border-border shadow-sm" data-testid={`stat-card-${i}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground mb-1">{s.title}</p>
                <p className="text-2xl font-heading font-bold text-foreground truncate">{s.value}</p>
                {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
                {s.delta !== undefined && <div className="mt-1"><DeltaBadge pct={s.delta} /></div>}
              </div>
              <div className={`w-11 h-11 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue comparison chart */}
      <div className="bg-white p-5 rounded-xl border border-border shadow-sm" data-testid="revenue-chart-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading font-semibold text-lg">Revenue · {mtd.month_label} vs {prev.month_label}</h2>
            <p className="text-xs text-muted-foreground">Daily comparison — dotted line = previous month</p>
          </div>
        </div>
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `RWF ${formatNumber(v)}`} labelFormatter={(l) => `Day ${l}`} />
              <Legend />
              <Line name={mtd.month_label} type="monotone" dataKey="this_month" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
              <Line name={prev.month_label} type="monotone" dataKey="previous_month" stroke="#9ca3af" strokeWidth={2} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Projections */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-5" data-testid="projection-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center border border-primary/20">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Month-end projections</h2>
            <p className="text-xs text-muted-foreground">Linear extrapolation from MTD × days_in_month / days_elapsed</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {projCards.map((p, i) => (
            <div key={i} className="bg-white p-3 rounded-lg border border-border" data-testid={`projection-${i}`}>
              <div className="text-xs text-muted-foreground">{p.title}</div>
              <div className={`text-xl font-bold ${p.tone} mt-1`}>RWF {formatNumber(p.v)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Low stock alert */}
      {stats?.low_stock_count > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5" data-testid="low-stock-alert">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-1">Low Stock Alert</h3>
              <p className="text-sm text-muted-foreground mb-3">{stats.low_stock_count} product(s) at or below their reorder level.</p>
              <div className="space-y-2">
                {stats.low_stock_products?.slice(0, 5).map((product, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-yellow-200">
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.category}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-semibold text-yellow-700">{product.current_stock}</p>
                      <p className="text-[10px] text-muted-foreground">Reorder at {product.reorder_level}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
